'use client';

import { BoxProps, Text } from '@mezzoforte/forge';
import { ReactNode, useContext } from 'react';
import { BiddingContext } from '@/context/BiddingContext';
import { SidebarSection, SidebarContent, SidebarDivider } from '@/features/EntryPage/SidebarSection';
import { useAuctionStatus } from '@/hooks/useAuctionStatus';
import { useHasAuctionEnded } from '@/hooks/useHasAuctionEnded';
import { useHermesEntry } from '@/hooks/useHermesEntry';
import { useSession } from '@/hooks/useSession';
import { Delivery } from '@/types/ApiResponse';
import { formatDateLong } from '@/util/date';
import { isAutobidActive, isUsersBidSurpassed, isUsersBidWinning } from '@/util/bid';
import { priceWithVat } from '@/util/price';
import { SmsNotificationToggle } from '@/components/Favorites/SmsNotificationToggle';
import { NetAuctionStatus } from '@/types/ListEntry';
import { EntryAuctionStatus, PreviewAuctionStatus } from '@/features/Bidding/AuctionStatus';
import { AutobidActive } from '@/features/Bidding/AutobidActive';
import { BiddingInputs } from '@/features/Bidding/BiddingInputs';
import { DeliveryInfo } from '@/features/Bidding/DeliveryInfo';
import EntryHighestBid, { PreviewHighestBid } from '@/features/Bidding/HighestBid';
import { BiddingLoginNotification } from '@/features/Bidding/BiddingLoginNotification';

interface BiddingBoxBodyProps extends BoxProps {
  delivery: Delivery | null;
  footerContent?: ReactNode;
}

interface EntryBiddingBoxBodyProps extends BiddingBoxBodyProps {
  isMinBidLocked: boolean;
  isEnded: boolean;
  isContinuous: boolean;
  isCancelled: boolean;
  bidCount: number;
  netAuctionStatus: NetAuctionStatus;
}

export function BiddingBoxBody({
  isMinBidLocked,
  isEnded,
  isContinuous,
  isCancelled,
  bidCount,
  netAuctionStatus,
  delivery,
  footerContent,
  ...boxProps
}: EntryBiddingBoxBodyProps) {
  const { entryId, minimumRaise, vatPerc, auctionStart, auctionEnd } = useContext(BiddingContext);
  const { hermesEntry } = useHermesEntry(entryId, vatPerc);
  const { currentUser, entryInfo } = useSession();
  const { isPending: loadingUserEntryInfo, data: userEntryInfo } = entryInfo(entryId);

  const loading = !hermesEntry || currentUser.isPending || loadingUserEntryInfo;
  const hasAuctionEnded = useHasAuctionEnded(isEnded);

  const { status: auctionStatus } = useAuctionStatus(
    entryId,
    new Date(auctionStart),
    new Date(hermesEntry?.auctionEnd ?? auctionEnd),
    hasAuctionEnded,
    hermesEntry?.isContinuous ?? isContinuous,
    hermesEntry?.isCancelled ?? isCancelled,
    hermesEntry?.bidCount ?? bidCount
  );

  const [biddingAllowed, reason, autobidActive] = (() => {
    if (hermesEntry?.isEnded ?? isEnded) return [false, ''];
    if (auctionStart > new Date()) return [false, ''];
    if (loading || !userEntryInfo?.isLoggedIn) return [true];

    if (isAutobidActive(userEntryInfo, hermesEntry.highestBid, minimumRaise, vatPerc)) {
      return [false, '', true];
    }

    if (userEntryInfo.isOwnConsumerNetAuction) {
      return [false, 'Toimeksiantaja ei voi osallistua omaan huutokauppaansa!'];
    }

    if (userEntryInfo.isPublisher) {
      return [false, 'Ilmoittaja ei voi osallistua omaan huutokauppaansa!'];
    }

    if (currentUser.data?.user && !currentUser.data?.user?.isBuyer) {
      if (currentUser.data?.user?.isAdmin || currentUser.data?.user?.isAccounting) {
        return [false, 'Ylläpitäjä ei voi osallistua huutokauppaan ilman ostajan roolia!'];
      }

      if (currentUser.data?.user?.isSeller) {
        return [false, 'Ilmoittaja ei voi osallistua huutokauppaan ilman ostajan roolia!'];
      }
    }

    return [true];
  })();

  const [showBidStatus, statusColor, statusText] = (() => {
    if (!hermesEntry?.bids) return [false];
    if (!userEntryInfo?.isLoggedIn) return [false];
    if (auctionStatus === 'cancelled') return [false];
    const { bidderId } = userEntryInfo;
    if (isUsersBidWinning(hermesEntry.bids, bidderId)) {
      return [true, 'success', 'Tarjouksesi on tällä hetkellä voittava!'];
    }
    if (isUsersBidSurpassed(hermesEntry.bids, bidderId)) {
      return [true, 'danger', 'Tarjouksesi ylitettiin'];
    }
    return [false];
  })();

  return (
    <SidebarSection
      {...boxProps}
      aria-live="polite"
      overflow="visible"
    >
      <EntryAuctionStatus
        loading={!hermesEntry}
        entryId={entryId}
        netAuctionStatus={netAuctionStatus}
        auctionStatus={auctionStatus}
        auctionEnd={new Date(hermesEntry?.auctionEnd ?? auctionEnd)}
      />
      <SidebarContent data-test="bidding-box">
        <EntryHighestBid />
        {showBidStatus && (
          <Text
            color={statusColor}
            fontSize="sm"
            fontWeight="bold"
            mb={3}
          >
            {statusText}
          </Text>
        )}
        {biddingAllowed && (
          <BiddingInputs
            isMinBidLocked={isMinBidLocked}
            showAutobid={userEntryInfo?.isLoggedIn === true}
          />
        )}
        {autobidActive && userEntryInfo?.isLoggedIn && userEntryInfo.biddingBot && (
          <AutobidActive
            entryId={entryId}
            maxBid={priceWithVat(userEntryInfo.biddingBot.maxBid, vatPerc)}
            minimumRaise={priceWithVat(minimumRaise, vatPerc)}
          />
        )}
        {!biddingAllowed && reason}
        {biddingAllowed && !userEntryInfo?.isLoggedIn && <BiddingLoginNotification />}
        <DeliveryInfo delivery={delivery} />
        {auctionStatus === 'scheduled' && <Text>Huutokauppa alkaa {formatDateLong(auctionStart)}</Text>}
        {!hermesEntry?.isEnded && (
          <Text mt={4}>
            Huutokauppa päättyy {formatDateLong(new Date(auctionEnd))}, tai 3 min viimeisen tarjouksen jälkeen
          </Text>
        )}
        {currentUser.data?.isAuthenticated && <SmsNotificationToggle entryId={entryId} />}
        {footerContent && <SidebarDivider>{footerContent}</SidebarDivider>}
      </SidebarContent>
    </SidebarSection>
  );
}

export function PreviewBiddingBoxBody({ delivery, footerContent, ...boxProps }: BiddingBoxBodyProps) {
  const { auctionStart, auctionEnd } = useContext(BiddingContext);
  return (
    <SidebarSection
      {...boxProps}
      aria-live="polite"
      overflow="visible"
    >
      <PreviewAuctionStatus />
      <SidebarContent data-test="preview-bidding-box">
        <PreviewHighestBid />
        <DeliveryInfo delivery={delivery} />
        <Text>Huutokauppa alkaa {formatDateLong(auctionStart)}</Text>
        <Text mt={4}>
          Huutokauppa päättyy {formatDateLong(new Date(auctionEnd))}, tai 3 min viimeisen tarjouksen jälkeen
        </Text>
        {footerContent && <SidebarDivider>{footerContent}</SidebarDivider>}
      </SidebarContent>
    </SidebarSection>
  );
}
